// Floating multi-select bulk-action toolbar (ClickUp-style).
//
// Renders a centered, bottom-docked dark chip when records are selected. Actions
// are provided by the caller (which routes them through ctx.patchMany /
// deleteMany, batchUpdateTasks, or moveTasksToPhase), so this component stays
// source-agnostic and reusable across the task surfaces.
//
// #793 Fall 4 — this is now the ONE bulk-action bar for all three task
// surfaces: personal tasks (routes/Lists.tsx), project tasks (views/TableView.tsx)
// and the cross-project hub (routes/MyTasks.tsx). Before this, each surface
// had its own hand-written bar; #878's assignee action already had to be
// added to all three by hand because of that duplication. The three surfaces
// are fachlich different (personal tasks have no assignees or phases; the
// hub selection can mix personal + project records and has no single
// project's status list or phase set; only project tasks have phases), so
// EVERY action here is opt-in via its own prop pair (`statuses`+`onSetStatus`,
// `onSetAssignees`, `phases`+`onSetPhase`, `onSetDone`, `onDelete`) — the host
// decides its action set by which props it passes, this component never
// guesses from context which actions "should" apply to it.
//
// `onSetPriority`, `count`, and `onClear` are the only actions common to all
// three hosts today, so they stay required.

import { Check, Flag, Trash2, X } from "lucide-preact";
import { TASK_PRIORITIES } from "@juuplan/shared";
import type { TaskPriority } from "@juuplan/shared";
import { TASK_PRIORITY_LABEL } from "../lib/format";
import type { StatusOption } from "../lib/taskRecord";
import { BulkAssigneeAction } from "./BulkAssigneeAction";

interface Props {
  count: number;
  busy?: boolean;
  /** Status options for the "set status" menu; omit to hide it. */
  statuses?: StatusOption[];
  onSetStatus?: (statusId: number) => void;
  /** Mark the selection done; omit to hide the action (project tasks have no
   *  bulk "done" toggle — the status action covers that surface instead). */
  onSetDone?: () => void;
  onSetPriority: (p: TaskPriority) => void;
  /** Replace the assignee set on every selected task with `ids` (one PATCH
   *  /api/tasks/batch request for the whole selection); omit to hide the
   *  action. */
  onSetAssignees?: (ids: number[]) => void;
  /** Distinct project ids across the current selection. Used to scope the
   *  assignee picker to that project's members when the whole selection
   *  belongs to a single project; a mixed/empty selection falls back to
   *  "all users" (safe: the server accepts any active user as an assignee
   *  regardless of project membership, so this never sends a value the
   *  server would reject for one of the projects, #878). */
  projectIds?: number[];
  /** Phase options for the "move to phase" menu (project tasks only); omit
   *  (or pass an empty array) to hide it. */
  phases?: { id: number; name: string }[];
  onSetPhase?: (phaseId: number) => void;
  /** Move the selection to trash; omit to hide the action (only personal
   *  tasks offer a bulk delete today). */
  onDelete?: () => void;
  onClear: () => void;
}

const CHIP =
  "inline-flex items-center gap-1.5 rounded px-2.5 py-1.5 text-xs text-[color:var(--color-inverse-on-surface)] hover:bg-[color:var(--color-inverse-on-surface)]/10 disabled:opacity-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-inverse-on-surface)]/40";

export function BulkActionToolbar(props: Props): preact.JSX.Element | null {
  const { busy = false } = props;

  if (props.count === 0) return null;

  return (
    <div
      class="juuplan-slide-up fixed bottom-[calc(56px+env(safe-area-inset-bottom)+0.75rem)] md:bottom-[max(1.5rem,env(safe-area-inset-bottom))] left-1/2 z-50 flex flex-wrap items-center justify-center gap-1 rounded-lg bg-[color:var(--color-inverse-surface)] px-2 py-2 shadow-level-3 max-w-[calc(100vw-1rem)]"
      role="toolbar"
      aria-label="Massenbearbeitung"
    >
      <span class="px-2 text-sm font-semibold text-[color:var(--color-inverse-on-surface)]">
        {props.count} ausgewählt
      </span>
      <span class="mx-1 h-5 w-px bg-[color:var(--color-inverse-on-surface)]/15" aria-hidden="true" />

      {props.onSetDone && (
        <button type="button" class={CHIP} disabled={busy} onClick={props.onSetDone}>
          <Check size={14} aria-hidden="true" /> Erledigt
        </button>
      )}

      {/* priority */}
      <span class="relative inline-flex">
        <span class={CHIP} aria-hidden="true">
          <Flag size={14} /> Priorität ▾
        </span>
        <select
          aria-label="Priorität setzen"
          disabled={busy}
          value=""
          onChange={(e) => {
            const v = (e.currentTarget as HTMLSelectElement).value;
            if (v) props.onSetPriority(v as TaskPriority);
            (e.currentTarget as HTMLSelectElement).value = "";
          }}
          class="absolute inset-0 opacity-0 cursor-pointer"
        >
          <option value="" disabled>
            Priorität…
          </option>
          {TASK_PRIORITIES.map((p) => (
            <option key={p} value={p}>
              {TASK_PRIORITY_LABEL[p]}
            </option>
          ))}
        </select>
      </span>

      {/* status */}
      {props.statuses && props.statuses.length > 0 && props.onSetStatus && (
        <span class="relative inline-flex">
          <span class={CHIP} aria-hidden="true">
            Status ▾
          </span>
          <select
            aria-label="Status setzen"
            disabled={busy}
            value=""
            onChange={(e) => {
              const v = (e.currentTarget as HTMLSelectElement).value;
              if (v) props.onSetStatus?.(Number(v));
              (e.currentTarget as HTMLSelectElement).value = "";
            }}
            class="absolute inset-0 opacity-0 cursor-pointer"
          >
            <option value="" disabled>
              Status…
            </option>
            {props.statuses.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </span>
      )}

      {/* assignees — REPLACE semantics only, see BulkAssigneeAction */}
      {props.onSetAssignees && (
        <BulkAssigneeAction
          onSetAssignees={props.onSetAssignees}
          projectIds={props.projectIds ?? []}
          resetKey={props.count}
          busy={busy}
          compact
          labelClassName={CHIP}
        />
      )}

      {/* phase (project tasks only) */}
      {props.phases && props.phases.length > 0 && props.onSetPhase && (
        <span class="relative inline-flex">
          <span class={CHIP} aria-hidden="true">
            Phase ▾
          </span>
          <select
            aria-label="In Phase verschieben"
            disabled={busy}
            value=""
            onChange={(e) => {
              const v = (e.currentTarget as HTMLSelectElement).value;
              if (v) props.onSetPhase?.(Number(v));
              (e.currentTarget as HTMLSelectElement).value = "";
            }}
            class="absolute inset-0 opacity-0 cursor-pointer"
          >
            <option value="" disabled>
              In Phase verschieben…
            </option>
            {props.phases.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </span>
      )}

      {props.onDelete && (
        <button
          type="button"
          class={`${CHIP} hover:!bg-[color:var(--color-error)]`}
          disabled={busy}
          onClick={props.onDelete}
        >
          <Trash2 size={14} aria-hidden="true" /> In den Papierkorb
        </button>
      )}

      <button
        type="button"
        class="ml-1 inline-flex items-center justify-center size-7 rounded text-[color:var(--color-inverse-on-surface)] hover:bg-[color:var(--color-inverse-on-surface)]/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-inverse-on-surface)]/40"
        onClick={props.onClear}
        aria-label="Auswahl aufheben"
      >
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );
}
